import {format, parse} from 'date-fns';
import {ptBR} from 'date-fns/locale';
import {OdontoBookingTaggingEnum} from 'main/analytics/gaTagsEnum';
import {DisabilityEnum, QueryParamsEnum} from 'presentation/constants';
import {ShiftTypeEnum} from 'presentation/modules/booking/exam/constants/shiftTypeEnum';
import {getOdontoStepPosition} from 'presentation/modules/booking/odonto/utils/getOdontoStepPosition';
import {
	MANUAL_DISTANCE_ORIGIN,
	MAX_DISTANCE_FILTER_UNITS_ODONTO,
	MIN_DISTANCE_IN_KM,
} from 'presentation/modules/booking/shared/constants/unitsInfo';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useDisabledSelectionDateStore} from 'presentation/modules/booking/shared/store/useDisabledSelectionDateStore';
import {useExternalOpenDateModalStore} from 'presentation/modules/booking/shared/store/useExternalOpenDateModalStore';
import {useOpenDrawerFilterStore} from 'presentation/modules/booking/shared/store/useOpenDrawerFilterStore';
import {useDistanceSliderStore} from 'presentation/modules/booking/shared/ui/components/DistanceSlider/store';
import {createPeriodsConfig} from 'presentation/modules/booking/shared/utils/createPeriodsConfig';
import {useQueryParams} from 'presentation/pages';
import {useEffect, useState} from 'react';
import {GeolocationFilterType, UseOdontoSearchBarProps} from './types';
import {useGetOdontoDatesQuery} from '../../../../pages/OdontoBooking/steps/OdontoProfessionalSelection/queries/useGetOdontoDatesQuery';
import {DrawerContentListByCategorySwitchProps} from 'presentation/components/Drawer/types';
import {translations} from 'presentation/translations-new';

const {disabilityAttendance, pcdService} = translations['pt-br'].commons;

export const useOdontoSearchBar = ({activeStep}: UseOdontoSearchBarProps) => {
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const openDrawerState = useOpenDrawerFilterStore(state => state.openDrawerState);
	const setOpenDrawerState = useOpenDrawerFilterStore(state => state.setOpenDrawerState);
	const distanceSlider = useDistanceSliderStore(state => state.distanceSlider);
	const setDistanceSlider = useDistanceSliderStore(state => state.setDistanceSlider);
	const {SEARCH_PARAMS, setSearchParams, clearSearch} = useQueryParams();
	const {query, shift, localityDistance, autocompleteQuery, disability} = SEARCH_PARAMS;
	const [shiftSelected, setShiftSelected] = useState<ShiftTypeEnum>(
		(shift as ShiftTypeEnum) ?? ShiftTypeEnum.ALL,
	);
	const externalOpenDateModal = useExternalOpenDateModalStore(
		state => state.externalOpenDateModal,
	);
	const setExternalOpenDateModal = useExternalOpenDateModalStore(
		state => state.setExternalOpenDateModal,
	);
	const disabledSelectionDate = useDisabledSelectionDateStore(
		state => state.disabledSelectionDate,
	);
	const [districtGeolocationState, setDistrictGeolocationState] = useState('');
	const [selectedFilterGeolocationState, setSelectedFilterGeolocationState] =
		useState<GeolocationFilterType>('distance');
	const [disabilityCheck, setDisabilityCheck] = useState(
		disability === DisabilityEnum.HAS_ACCESSIBILITY,
	);

	const {isManualLocation, districtName, dateAt} = bookingData;

	const {PROFESSIONAL_STEP, UNIT_STEP, SPECIALITY_OR_EXAM_STEP, DISTRICT_STEP} =
		getOdontoStepPosition({
			isManualLocation: isManualLocation,
		});

	const {
		data: odontoAvailableDatesList,
		isFetching: odontoDatesIsLoading,
		isError: odontoDatesIsError,
	} = useGetOdontoDatesQuery({
		enabled: activeStep === PROFESSIONAL_STEP,
	});

	const showOdontoUnitsDrawer = activeStep === UNIT_STEP && Boolean(isManualLocation);
	const showGeolocationFilterDrawer = !isManualLocation && activeStep === UNIT_STEP;
	const showFilter = showOdontoUnitsDrawer || showGeolocationFilterDrawer;
	const distanceFilterAtMaxDistance =
		Number(localityDistance) >= MAX_DISTANCE_FILTER_UNITS_ODONTO;

	const isDisabilityFilterActive = disability === DisabilityEnum.HAS_ACCESSIBILITY;

	const distanceFilterIsActive = localityDistance && localityDistance !== MIN_DISTANCE_IN_KM;
	const districtGeolocationFilterIsActive =
		!isManualLocation && districtName && activeStep === UNIT_STEP;
	const filterIsActive =
		isDisabilityFilterActive || distanceFilterIsActive || districtGeolocationFilterIsActive;
	const drawerButtonTagId = OdontoBookingTaggingEnum.CLICK_FILTRAR_UNIDADES;

	const showFilterByLetter = !query && activeStep === SPECIALITY_OR_EXAM_STEP;
	const showCurrentLocation = activeStep === UNIT_STEP;
	const showProfessionalFilters = activeStep === PROFESSIONAL_STEP;
	const shouldClearShiftFilter = activeStep === UNIT_STEP;
	const shouldClearDisability =
		(activeStep === DISTRICT_STEP && isManualLocation) ||
		(activeStep === SPECIALITY_OR_EXAM_STEP && !isManualLocation);

	const availableDatesLoadingOrError = odontoDatesIsLoading || odontoDatesIsError;
	const availableDates = availableDatesLoadingOrError ? [] : odontoAvailableDatesList;

	const currentSelectedDate = dateAt ? parse(dateAt, 'P', 0, {locale: ptBR}) : undefined;

	const PERIODS = createPeriodsConfig({
		shiftSelected,
		onShiftChange: (shift: ShiftTypeEnum) => setShiftSelected(shift),
	});

	const SERVING_PEOPLE_WITH_DISABILITIES: DrawerContentListByCategorySwitchProps = {
		categoryTitle: pcdService,
		categoryItemsList: [
			{
				check: disabilityCheck,
				title: disabilityAttendance,
				onCheck: () => setDisabilityCheck(state => !state),
				icon: 'accessibility-filled',
			},
		],
	};

	const handleOpenDrawer = () => {
		setOpenDrawerState(true);
	};

	const handleCloseDrawer = (abandon?: boolean) => {
		setOpenDrawerState(false);
		if (abandon) {
			setShiftSelected(ShiftTypeEnum.ALL);
			setDistanceSlider(Number(localityDistance));

			if (isDisabilityFilterActive) {
				setDisabilityCheck(true);
			} else {
				setDisabilityCheck(false);
			}

			if (districtGeolocationFilterIsActive) {
				setSelectedFilterGeolocationState('district');
				setDistrictGeolocationState(bookingData.districtName ?? '');
			} else {
				setSelectedFilterGeolocationState('distance');
				setDistrictGeolocationState('');
			}

			if (distanceFilterIsActive) {
				setSelectedFilterGeolocationState('distance');
			}
		}
	};

	const handleClearDistance = () => {
		setDistanceSlider(0);
		setSearchParams(prev => {
			prev.set(QueryParamsEnum.LOCALITY_DISTANCE, '0');
			prev.delete(QueryParamsEnum.DISTANCE_ORIGIN);
			return prev;
		});
		handleCloseDrawer();
	};

	const handleConfirmDistance = () => {
		setSearchParams(prev => {
			prev.set(QueryParamsEnum.LOCALITY_DISTANCE, distanceSlider.toString());
			prev.set(QueryParamsEnum.DISTANCE_ORIGIN, MANUAL_DISTANCE_ORIGIN);
			return prev;
		});

		handleCloseDrawer();
	};

	const handleShiftButton = (shiftType: ShiftTypeEnum | string) => {
		setSearchParams(
			prev => {
				prev.set(QueryParamsEnum.SHIFT, shiftType);
				return prev;
			},
			{
				replace: true,
			},
		);
	};

	const handleClearShift = () => {
		setShiftSelected(ShiftTypeEnum.ALL);
		handleShiftButton(ShiftTypeEnum.ALL);
		handleCloseDrawer();
	};

	const handleConfirmShift = () => {
		handleShiftButton(shiftSelected);
		handleCloseDrawer();
	};

	const handleChangeDate = (selectedDate: Date | null) => {
		setSearchParams(prev => {
			if (selectedDate) {
				setBookingData(prevValue => ({
					...prevValue,
					dateAt: format(selectedDate, 'P', {locale: ptBR}),
				}));

				prev.set(QueryParamsEnum.CALENDAR_ACTIVE, 'true');
			} else {
				setBookingData(prevValue => ({
					...prevValue,
					dateAt: '',
				}));

				prev.delete(QueryParamsEnum.CALENDAR_ACTIVE);
			}

			return prev;
		});
		setExternalOpenDateModal(false);
	};

	const clearSearchParams = () => {
		setSearchParams(
			prev => {
				prev.delete(QueryParamsEnum.SEARCH_QUERY);
				prev.delete(QueryParamsEnum.AUTOCOMPLETE_QUERY);
				prev.delete(QueryParamsEnum.SEARCH_BY_LETTER);

				return prev;
			},
			{
				replace: true,
			},
		);
	};

	const handleClearShiftAndSearch = () => {
		setShiftSelected(ShiftTypeEnum.ALL);
		handleShiftButton(ShiftTypeEnum.ALL);
		handleCloseDrawer();
		clearSearchParams();
		if (clearSearch) {
			clearSearch();
		}
		setSearchParams(prev => {
			setBookingData(prevValue => ({
				...prevValue,
				dateAt: '',
			}));

			prev.set(QueryParamsEnum.SEARCH_QUERY, 'clear');
			prev.delete(QueryParamsEnum.CALENDAR_ACTIVE);
			if (autocompleteQuery) prev.delete(QueryParamsEnum.AUTOCOMPLETE_QUERY);
			return prev;
		});
	};

	const handleCheck = (checked: boolean) => {
		setSearchParams(
			prev => {
				prev.set(
					QueryParamsEnum.DISABILITY,
					checked ? DisabilityEnum.HAS_ACCESSIBILITY : DisabilityEnum.ALL,
				);
				return prev;
			},
			{
				replace: true,
			},
		);
	};

	const handleClearDisability = () => {
		setDisabilityCheck(false);
		handleCheck(false);
		handleCloseDrawer();
	};

	const handleConfirmDisability = () => {
		handleCheck(disabilityCheck);
		handleCloseDrawer();
	};

	useEffect(() => {
		if (shouldClearDisability) {
			handleClearDisability();
		}

		if (!isManualLocation && activeStep === SPECIALITY_OR_EXAM_STEP) {
			setDistrictGeolocationState('');
			setSelectedFilterGeolocationState('distance');
		}
		if (shouldClearShiftFilter) {
			handleClearShift();
		}
	}, [activeStep]);

	useEffect(() => {
		if (SEARCH_PARAMS.shift === '') {
			handleCloseDrawer(!openDrawerState);
		}
	}, [SEARCH_PARAMS.shift]);

	return {
		showFilter,
		handleOpenDrawer,
		filterIsActive,
		drawerButtonTagId,
		openDrawerState,
		showGeolocationFilterDrawer,
		handleCloseDrawer,
		distanceFilterAtMaxDistance,
		handleConfirmDistance,
		handleClearDistance,
		districtGeolocationState,
		setDistrictGeolocationState,
		selectedFilterGeolocationState,
		setSelectedFilterGeolocationState,
		showFilterByLetter,
		showCurrentLocation,
		showProfessionalFilters,
		shift,
		PERIODS,
		handleClearShift,
		handleConfirmShift,
		setExternalOpenDateModal,
		externalOpenDateModal,
		availableDates,
		currentSelectedDate,
		handleChangeDate,
		disabledSelectionDate,
		handleClearShiftAndSearch,
		SERVING_PEOPLE_WITH_DISABILITIES,
		showOdontoUnitsDrawer,
		handleClearDisability,
		handleConfirmDisability,
	};
};
